import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PinnedLetters, GalleryGrid, DecalSpawner, ColorFilterPill } from '../components';
import { GalleryItem, ArtworkColor } from '../types/gallery';
import { getOptimizedUrl, getCachedGalleryItems, fetchGalleryItems } from '../utils/utils';
import { saveMainPageScroll, restoreMainPageScroll } from '../utils/scrollRestoration';
import { saveActiveColorFilter, getSavedColorFilter, isPageReload, clearActiveColorFilter } from '../utils/colorFilterStorage';
import './HomePage.css';

export const HomePage: React.FC = () => {
  const location = useLocation();
  const isReload = isPageReload();
  const isFromReturnLink = Boolean(
    !isReload && location.state && typeof location.state === 'object' && (location.state as any).fromReturnLink
  );

  // If directly opened / refreshed (F5), clear the saved active filter so it starts fresh with all items.
  if (!isFromReturnLink) {
    clearActiveColorFilter();
  }

  const initialFilter = isFromReturnLink ? getSavedColorFilter() : null;

  const cached = getCachedGalleryItems();
  const [items, setItems] = useState<GalleryItem[]>(() => cached || []);
  const [activeColor, setActiveColor] = useState<ArtworkColor | null>(() => initialFilter);
  const [rowCount, setRowCount] = useState<number>(() => {
    if (!cached) return 34;
    const count = initialFilter ? cached.filter((i) => i.color === initialFilter).length : cached.length;
    return Math.max(1, Math.ceil(count / 3));
  });

  useEffect(() => {
    // Clean any legacy search params from URL bar so F5 / reload is clean
    if (typeof window !== 'undefined' && window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!cached || cached.length === 0) {
      fetchGalleryItems()
        .then((data: GalleryItem[]) => {
          if (!isMounted) return;
          setItems(data);
          const filtered = initialFilter ? data.filter((i) => i.color === initialFilter) : data;
          const rows = Math.max(1, Math.ceil(filtered.length / 3));
          setRowCount(rows);
        })
        .catch((err) => {
          console.error('Failed to load gallery data:', err);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [cached, initialFilter]);

  // Compute items filtered by selected color tag
  const filteredItems = useMemo(() => {
    if (!activeColor) return items;
    return items.filter((item) => item.color === activeColor);
  }, [items, activeColor]);

  // When a filter is toggled, remember it in session storage and adjust view
  const handleSelectColor = (color: ArtworkColor | null) => {
    setActiveColor(color);
    saveActiveColorFilter(color);

    const pillElement = document.querySelector('.color-filter-nav');
    if (pillElement) {
      const pillTop = pillElement.getBoundingClientRect().top + window.scrollY;
      if (window.scrollY > pillTop) {
        window.scrollTo({ top: Math.max(0, pillTop - 18), behavior: 'smooth' });
      }
    }
  };

  // Track and save scroll depth while navigating or leaving the main page
  useEffect(() => {
    const handleScroll = () => {
      saveMainPageScroll();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', saveMainPageScroll);

    return () => {
      // User is navigating away from the main exhibition page
      saveMainPageScroll();
      saveActiveColorFilter(activeColor);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', saveMainPageScroll);
    };
  }, [activeColor]);

  // Restore previous scroll level whenever grid items are ready
  useLayoutEffect(() => {
    if (items.length > 0) {
      restoreMainPageScroll();
    }
  }, [items.length]);


  const totalUnfilteredRows = Math.max(1, Math.ceil((items.length || cached?.length || 105) / 3));

  return (
    <div className="home-page-container" style={{ position: 'relative', width: '100%', minHeight: '100vh', overflow: 'visible' }}>
      {/* Background Decal Spawner repeating all the way to the bottom */}
      <DecalSpawner rowCount={rowCount} totalRowCount={totalUnfilteredRows} />

      {/* Header section with brand image and interactive pinned letters */}
      <header className="header">
        <img
          src="https://ik.imagekit.io/schzstrg/tr:w-1440,q-100/schzheader_new.png"
          className="header-image"
          alt="schzstrg"
        />
        <PinnedLetters phrase={"iNTERACTIVE\nART EXHIBITION"} />
      </header>

      {/* Sliding color filter pill placed directly above the exhibition feed */}
      {items.length > 0 && (
        <>
          <ColorFilterPill
            activeColor={activeColor}
            onSelectColor={handleSelectColor}
            items={items}
          />

          <GalleryGrid
            items={filteredItems}
            getOptimizedUrl={getOptimizedUrl}
            onRowCountCalculated={setRowCount}
          />
        </>
      )}
    </div>
  );
};

export default HomePage;
